import React, { useState, useRef, useLayoutEffect, useContext } from "react";
import { Link } from "gatsby";
import { motion, useMotionValue } from "framer-motion";

import { PlaylistContext } from "./playlist";
import { InlineVideo } from "@components/Video";
import Star from "@components/Star";

import { css } from "@oddcommon/utils";
import styles from "./WorkGrid.module.scss";

export default function GridLayout({ items, oneRow = false, className }) {
  const measureHeightRef = useRef();
  const [playingVideo, setPlayingVideo] = useState(null);
  const { actions, currentPlaylist } = useContext(PlaylistContext);

  // Image height is the width of the reference element (which is posiitoned on a grid)
  const imageHeight = useMotionValue("0px");
  useLayoutEffect(() => {
    const update = () => imageHeight.set(`${measureHeightRef.current.offsetWidth}px`);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [imageHeight]);

  return (
    <>
      <div className={styles.grid}>
        <div className={styles.reference} ref={measureHeightRef} />
      </div>

      <motion.div
        className={css(styles.container, oneRow && styles.oneRow, className)}
        style={{ "--image-height": imageHeight }}
      >
        {items.map(({ slug, client, title, thumbnail, description, accentColor, awards }, i) => {
          // determines if item will render as a square on desktop
          const isSquare = (i + 1) % 10 !== 1 && (i + 1) % 10 !== 7;
          return (
            <article
              style={{ "--page-accent-color": accentColor }}
              onMouseEnter={() => setPlayingVideo(slug)}
              onMouseLeave={() => setPlayingVideo(null)}
              className={styles.item}
              data-work-square={isSquare}
              data-work-rectangle={!isSquare}
              key={slug}
            >
              <Link
                to={`/work/project/${slug}`}
                data-cursor-color={accentColor}
                className={styles.thumbnail}
              >
                <div className={styles.wrapper}>
                  {thumbnail.type === "video" && (
                    <InlineVideo
                      src={thumbnail.src}
                      poster={thumbnail.poster}
                      webp={thumbnail.webp}
                      pause={playingVideo !== undefined && playingVideo !== slug}
                      cover
                      alt={thumbnail.alt ? thumbnail.alt : description}
                      awards={awards}
                    />
                  )}
                  {thumbnail.type === "image" && (
                    <img
                      src={thumbnail.poster + "?fm=webp&w=1920&q=80"}
                      alt={thumbnail.alt ? thumbnail.alt : description}
                    />
                  )}
                </div>
              </Link>
              <div className={styles.meta}>
                <div
                  className={css(
                    styles.save,
                    currentPlaylist.findIndex((p) => {
                      return p === slug;
                    }) > -1 && styles.active,
                  )}
                  onClick={() => {
                    actions.addRemovePlaylistItem(slug);
                  }}
                >
                  <Star spec={{ size: "0.55em", stroke: "0.07em" }} />
                </div>
                <div className={styles.content}>
                  <p className={styles.client}>{client}</p>
                  <h5>{title}</h5>
                  <p>{description}</p>
                  <Link to={`/work/project/${slug}`}>View Project</Link>
                </div>
              </div>
            </article>
          );
        })}
      </motion.div>
    </>
  );
}
